import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { fetchPublicCompanyProfile } from "@/services/companyProfile";

const DEFAULT_MESSAGE = "系統維護中，商品瀏覽不受影響，但下單結帳、會員註冊登入、預約與聯絡表單暫停服務，造成不便敬請見諒。";

/**
 * 全站維護模式的前台提示。跟多數有規模的電商（Amazon、momo、蝦皮等）做法一致，採「軟性維護」：
 * 後端更新期間，商品瀏覽、搜尋、加入購物車等唯讀功能照常開放，不整頁鎖住前台（避免流量、SEO、
 * 使用者體驗被無謂犧牲），只有下單結帳、會員註冊登入、預約、聯絡表單等會寫入資料庫的操作會被
 * 後端 API 暫停（見 MaintenanceModeSubscriber），這裡負責在使用者實際點下去之前，先用一個
 * 不擋畫面的浮動提示卡告知現況，讓使用者不會白白填完表單才發現無法送出。
 *
 * 後台管理（/admin、/admin-login）不受影響，不顯示此提示。
 */
export default function MaintenanceNotice() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchPublicCompanyProfile().then((profile) => {
      if (!alive) return;
      setActive(profile?.isMaintenanceMode ?? false);
      setMessage(profile?.maintenanceMessage || DEFAULT_MESSAGE);
    });
    return () => {
      alive = false;
    };
  }, []);

  const isAdminRoute = location.pathname.startsWith("/admin");
  if (isAdminRoute || !active || dismissed) {
    return null;
  }

  return (
    <div className="fixed inset-x-4 bottom-4 z-40 sm:inset-x-auto sm:left-6 sm:max-w-sm">
      <div className="flex items-start gap-2.5 rounded-xl border border-[#f0ddb0] bg-[#fdf9ef] px-4 py-3 shadow-lg shadow-black/10">
        <svg className="mt-0.5 shrink-0 text-[#8a6a2f]" fill="none" height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
          />
        </svg>
        <p className="flex-1 text-sm font-medium leading-relaxed text-[#5c4a22]">{message}</p>
        <button
          aria-label="關閉提示"
          className="shrink-0 text-[#8a6a2f]/70 hover:text-[#8a6a2f]"
          onClick={() => setDismissed(true)}
          type="button"
        >
          <svg fill="none" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          </svg>
        </button>
      </div>
    </div>
  );
}
